import React, { useState, useEffect, useLayoutEffect } from "react";
import { useFormik } from "formik";
import Modal from "components/UI/Modal/Modal";
import { InsertModal, UpdateModal, DeleteModal } from "./Actions";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { routeInfo, userCredentials } from "recoilState/GlobalState";
import { HeaderButton } from "components";
import Filters from "components/Filters";
import Add from "@mui/icons-material/Add";
import Cached from "@mui/icons-material/Cached";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import FilterAltOffRoundedIcon from '@mui/icons-material/FilterAltOffRounded';
import { Buttons } from "components";
import { ACTIONS } from "consts/Actions";
import { seriesFeriadosTitles as titles } from "consts/titulos-de-tablas";
import { TIPOS_FERIADO, labelDeTipoFeriado } from "consts/tipos-feriado";
import styles from "styles/pages/Pages.module.scss";
import { useService, puedeEditarServicio } from "components/route/ServiceRoute";
import XGrid from "components/UI/Grid/XGrid";
import { RNHFeriadoSerieGet } from "services/services-mia/programa-salud/feriados/RNHFeriadoSerieGet";

/**
 * La vista arranca mostrando el AÑO EN CURSO.
 *
 * Sin rango, el Get trae todas las fechas de todos los feriados de todos los años
 * juntas —un feriado que se repite tiene un registro por año—, y la grilla abre con
 * cientos de filas donde lo que casi siempre se busca es lo de este año.
 *
 * Es una FUNCION y no una constante de modulo: calculado una sola vez al importar, un
 * navegador que quedo abierto de un 31 de diciembre al 1 de enero seguiria filtrando
 * por el año viejo.
 *
 * Sin zona horaria de por medio: lo unico que se necesita es el año, y los dos bordes
 * son fechas fijas del calendario.
 */
const filtrosPorDefecto = () => {
  const anio = new Date().getFullYear();
  return { desde: `${anio}-01-01`, hasta: `${anio}-12-31`, tipo: "" };
};

export default function SeriesFeriados() {
  const credentials = useRecoilValue(userCredentials);
  const setRoute = useSetRecoilState(routeInfo);
  const [modalDetails, setModalDetails] = useState({ isOpen: false });
  const [loading, setLoading] = useState(false);
  const [series, setSeries] = useState([]);
  const [rowSelected, setRowSelected] = useState({ status: false, row: {} });
  const { service } = useService();

  const getSeries = async (filtros = filtrosPorDefecto()) => {
    setSeries([]);
    setRowSelected({ status: false, row: {} });
    setLoading(true);

    const respuesta = await RNHFeriadoSerieGet(credentials, {
      desde: filtros.desde,
      hasta: filtros.hasta,
      tipo: filtros.tipo,
    });
    setLoading(false);
    if (!respuesta.ok) return;

    const newRows = respuesta.data
      // Filtra la fila centinela que el back manda cuando no hay registros.
      .filter((data) => data?.rnhFeriadoSerieId)
      .map((data) => ({
        id: data.rnhFeriadoSerieId,
        feriado: data.rnhFeriadoNombre ?? "",
        tipo: labelDeTipoFeriado(data.rnhFeriadoTipo),
        fecha: data.rnhFeriadoSerieFecha || null,
        feriadoId: data.rnhFeriadoId ?? data.rnhFeriadoSerieFeriadoId ?? "",
        version: data.rnhFeriadoSerieVersion,
      }));

    setSeries(newRows);
  }

  // `useFormik` y no `<Formik>`: es lo que espera el componente Filters, que recibe la
  // instancia entera y arma los inputs desde `inputsRender`.
  const formik = useFormik({
    initialValues: filtrosPorDefecto(),
    onSubmit: (values) => {
      getSeries(values);
    },
  });

  /**
   * Vuelve al año en curso, no a vacio.
   *
   * Vaciar el rango traeria todos los años de todos los feriados, que es justo lo que
   * el default evita. "Borrar" aca significa descartar lo que el usuario cambio y
   * volver a como abre la pantalla.
   */
  const borrarFiltros = () => {
    const porDefecto = filtrosPorDefecto();
    formik.setValues(porDefecto);
    getSeries(porDefecto);
  };

  const closeModal = () => {
    setModalDetails({ isOpen: false });
  };
  const handleClick = ({ procede, title }) => {
    setModalDetails({ procede, title, isOpen: true });
  };

  // Refrescar tiene que respetar lo que hay filtrado: si volviera a traer todo, el
  // usuario perdería el recorte después de cada alta o modificación.
  const refrescar = () => getSeries(formik.values);

  useEffect(() => {
    getSeries()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useLayoutEffect(() => {
    setRoute({
      wordKey: `Series de Feriados - Programa Salud RN`
    })
  }, [service.servicioNombre, setRoute])

  return (
    <main className={styles.main}>
      <Modal
        size={modalDetails.procede === ACTIONS.DELETE ? 'sm' : 'md'}
        open={modalDetails.isOpen}
        title={modalDetails.title}
        onClose={closeModal}
      >
        {modalDetails.procede === ACTIONS.UPDATE && (
          <UpdateModal
            row={rowSelected.row}
            updateList={refrescar}
            closeModal={closeModal}
          />
        )}
        {modalDetails.procede === ACTIONS.DELETE && (
          <DeleteModal
            row={rowSelected.row}
            updateList={refrescar}
            closeModal={closeModal}
            setterRowSelected={setRowSelected}
          />
        )}
        {modalDetails.procede === ACTIONS.INSERT && (
          <InsertModal
            updateList={refrescar}
            closeModal={closeModal}
          />
        )}
      </Modal>
      <div className={styles["filters-actions"]}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Filters
            formik={formik}
            inputsRender={[
              { type: "date", name: "desde", label: "Desde", disabled: loading },
              { type: "date", name: "hasta", label: "Hasta", disabled: loading },
              {
                type: "select",
                name: "tipo",
                label: "Tipo",
                disabled: loading,
                options: TIPOS_FERIADO,
                // Los tres apuntan a la misma const local: `cod` es la clave y el
                // valor que viaja al servicio, `label` lo que se ve.
                firstOptionKey: "cod",
                firstOptionValue: "cod",
                firstOptionName: "label",
              },
            ]}
            disabled={loading}
          />
          <HeaderButton
            text={"Borrar filtros"}
            onClick={borrarFiltros}
            disabled={loading}
          >
            <FilterAltOffRoundedIcon />
          </HeaderButton>
        </div>
        <Buttons>
          <HeaderButton
            text={"Actualizar"}
            onClick={refrescar}
            disabled={rowSelected.row.id || false}
          >
            <Cached />
          </HeaderButton>
          <HeaderButton
            text={"Nueva Fecha"}
            handleClick={handleClick}
            data={{
              title: "Nueva Fecha de Feriado",
              procede: ACTIONS.INSERT
            }}
            disabled={!puedeEditarServicio(service) || rowSelected.row.id}
          >
            <Add />
          </HeaderButton>
          <HeaderButton
            text={"Modificar Fecha"}
            handleClick={handleClick}
            data={{
              title: "Modificar Fecha de Feriado",
              procede: ACTIONS.UPDATE
            }}
            disabled={!puedeEditarServicio(service) || (!rowSelected.row.id)}
          >
            <EditOutlined />
          </HeaderButton>
          <HeaderButton
            text={"Eliminar Fecha"}
            handleClick={handleClick}
            data={{
              title: "Eliminar Fecha de Feriado",
              procede: ACTIONS.DELETE
            }}
            disabled={!puedeEditarServicio(service) || (!rowSelected.row.id)}
          >
            <DeleteOutlineRoundedIcon />
          </HeaderButton>
        </Buttons>
      </div>
      <div className={styles.container}>
        <XGrid
          rows={series}
          titles={titles}
          loading={loading}
          secondarySelected={rowSelected}
          setSecondarySelected={setRowSelected}
        />
      </div>
    </main>
  );
}
